import React, { useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { Box, Typography, Button } from '@material-ui/core';
import * as Yup from 'yup';
import BarberShopTitle from '../../components/BarberShopTitle';
import getValidationErrors, { IErrors } from '../../utils/getValidationErrors';
import Service from './components/Service';
import { IService } from '../../shared/interfaces/service-interfaces';
import {
  useGetCompanyDetail,
  useGetCompanyServices,
} from '../../hooks/data/companies';
import CustomForm from '../../components/CustomForm';
import { form, schema } from './form';
import { useOrder } from '../../hooks/order';
import Loading from '../../components/Loading';

type IParams = {
  slug: string;
};

type BarberShopFormData = {
  name: string;
  service: IService;
  whatsapp: string;
};

const initialFormData = {
  name: '',
  whatsapp: '',
  service: {
    id: '',
    time_box: 0,
    price: '',
    service_name: '',
  },
};

const BarberShop: React.FC = () => {
  const params = useParams<IParams>();
  const history = useHistory();
  const { updateOrderData } = useOrder();

  const { data: servicesData, status: servicesStatus } = useGetCompanyServices(
    params.slug,
  );

  const { data: companyData } = useGetCompanyDetail(params.slug);

  const [data, setData] = useState<BarberShopFormData>(initialFormData);

  const [selectedService, setSelectedService] = useState<IService>();

  const [errors, setErrors] = useState<IErrors>({
    name: '',
    service: '',
    whatsapp: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({
      ...data,
      [e.target.name]:
        e.target.name === 'whatsapp'
          ? e.target.value.replace(/\D+/g, '')
          : e.target.value,
    });
    setErrors({
      ...errors,
      [e.target.name]: '',
    });
  };

  const handleClickService = useCallback(
    (service: IService) => () => {
      setSelectedService(service);
      setData({
        ...data,
        service,
      });
      setErrors({
        ...errors,
        service: '',
      });
    },
    [data, errors],
  );

  const handleSubmit = useCallback(async () => {
    try {
      await schema.validate(data, {
        abortEarly: false,
      });

      updateOrderData({
        customer_name: data.name,
        customer_whatsapp: data.whatsapp,
        service: data.service,
      });

      setData(initialFormData);
      setErrors({});
      history.push(`/parceiro/${params.slug}/agendamento`);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors = getValidationErrors(err);

        setErrors(validationErrors);
      }
    }
  }, [data, history, params.slug, updateOrderData]);
  const getServicesContent = useCallback(() => {
    if (servicesData?.length === 0) {
      return <Typography>Não há serviços cadastrados até o momento</Typography>;
    }

    return servicesData?.map(row => (
      <Service
        service={row}
        selected={row.service_name === selectedService?.service_name}
        handleClick={handleClickService(row)}
      />
    ));
  }, [handleClickService, selectedService?.service_name, servicesData]);

  if (servicesStatus === 'loading') {
    return <Loading />;
  }

  if (servicesStatus !== 'success') {
    return <p>Erro ao buscar os serviços. Tente novamente</p>;
  }

  return (
    <Box
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      pb={2}
      maxWidth="720px"
      maxHeight="100vh"
      marginX="auto"
    >
      <Box p={3} pb={0}>
        <BarberShopTitle title={companyData?.company_name ?? ''} />
        <Typography>
          {`Seja bem-vindo ao sistema de agendamento da ${companyData?.company_name.toLocaleUpperCase()}. Informe o seu nome abaixo e escolha o serviço pretendido`}
        </Typography>
      </Box>
      <Box width="100%" marginY={2} px={2}>
        <CustomForm
          onChange={handleChange}
          data={data}
          form={form}
          errors={errors}
        />
      </Box>
      <Box
        flex="1"
        width="100%"
        marginBottom={2}
        overflow="auto"
        px={3}
        minHeight="200px"
      >
        {getServicesContent()}
      </Box>
      <Box textAlign="start" width="100%" p={1}>
        {!!errors.service && (
          <Typography variant="caption" color="error">
            {errors.service}
          </Typography>
        )}
      </Box>
      <Box
        pt={2}
        borderTop="2px solid"
        display="flex"
        justifyContent="center"
        width="100%"
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!selectedService}
        >
          Avançar
        </Button>
      </Box>
    </Box>
  );
};

export default BarberShop;
